import { useCallback, useContext, useMemo, useState } from 'react';

import { useMeQuery } from '@blockpulse3/graphql/hooks';

import { IUserContext, UserContext } from './context';

export type UserContextProviderProps = {
  children: React.ReactNode;
};

/**
 * UserProvider.
 *
 * Custom UserContext provider, exposing user state
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function UserContextProvider({ children }: UserContextProviderProps): JSX.Element {
  const [status, setStatus] = useState<IUserContext['status']>('loading');

  const { data, refetch } = useMeQuery({
    fetchPolicy: 'no-cache',
    onCompleted: () => {
      setStatus('authenticated');
    },
    onError: () => {
      setStatus('unauthenticated');
    },
  });

  const loadUser = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const value: IUserContext = useMemo(
    () => ({ user: data?.me || null, status, setStatus, loadUser }),
    [data, status, loadUser],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

/**
 * useUser.
 *
 * Custom hook to retrieve UserContext
 *
 * @returns {IUserContext}
 */
export function useUser(): IUserContext {
  return useContext(UserContext);
}
